import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Link as LinkIcon,
  Public as PublicIcon,
} from '@mui/icons-material';

export type CompanyInfoProvider = 'linkedin' | 'indeed' | 'xing' | 'cylex' | 'unknown';

export interface CompanyInfoImportDraft {
  provider: CompanyInfoProvider;
  url: string;
}

type ProviderConfig = {
  id: Exclude<CompanyInfoProvider, 'unknown'>;
  label: string;
  hint: string;
  domains: string[];
};

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    hint: 'Company page URL',
    domains: ['linkedin.com'],
  },
  {
    id: 'indeed',
    label: 'Indeed',
    hint: 'Company page URL',
    domains: ['indeed.com'],
  },
  {
    id: 'xing',
    label: 'XING',
    hint: 'Company profile URL',
    domains: ['xing.com'],
  },
  {
    id: 'cylex',
    label: 'Cylex',
    hint: 'Business listing URL',
    domains: ['cylex', 'cylex.de', 'cylex.com'],
  },
];

const normalizeUrl = (raw: string) => {
  const input = (raw || '').trim();
  if (!input) return '';
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(input)) return input;
  if (input.startsWith('www.')) return `https://${input}`;
  return `https://www.${input}`;
};

const detectProviderFromUrl = (rawUrl: string): CompanyInfoProvider => {
  const normalized = normalizeUrl(rawUrl);
  if (!normalized) return 'unknown';

  try {
    const hostname = new URL(normalized).hostname.toLowerCase();
    const match = PROVIDERS.find(p => p.domains.some(d => hostname.includes(d)));
    return match?.id ?? 'unknown';
  } catch {
    return 'unknown';
  }
};

export interface CompanyInfoImportModalProps {
  open: boolean;
  initialValue?: Partial<CompanyInfoImportDraft>;
  onClose: () => void;
  onConfirm: (value: CompanyInfoImportDraft) => void;
  onScrape?: (value: CompanyInfoImportDraft) => Promise<void>;
}

const CompanyInfoImportModal: React.FC<CompanyInfoImportModalProps> = ({
  open,
  initialValue,
  onClose,
  onConfirm,
  onScrape,
}) => {
  const [provider, setProvider] = useState<CompanyInfoProvider>(initialValue?.provider || 'unknown');
  const [url, setUrl] = useState<string>(initialValue?.url || '');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setProvider(initialValue?.provider || 'unknown');
    setUrl(initialValue?.url || '');
    setTouched(false);
    setSubmitting(false);
    setSubmitError(null);
  }, [open, initialValue?.provider, initialValue?.url]);

  const detectedProvider = useMemo(() => detectProviderFromUrl(url), [url]);

  useEffect(() => {
    if (!touched) return;
    if (provider === 'unknown' && detectedProvider !== 'unknown') {
      setProvider(detectedProvider);
    }
  }, [detectedProvider, provider, touched]);

  const canContinue = useMemo(() => {
    const normalized = normalizeUrl(url);
    if (!normalized) return false;
    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  }, [url]);

  const providerLabel = useMemo(() => {
    if (provider === 'unknown') return 'Auto-detect';
    return PROVIDERS.find(p => p.id === provider)?.label ?? 'Unknown';
  }, [provider]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              background: 'linear-gradient(135deg, rgba(255, 122, 24, 0.95) 0%, rgba(255, 61, 0, 0.9) 100%)',
              boxShadow: '0 14px 30px rgba(255, 61, 0, 0.18)',
              flexShrink: 0,
            }}
          >
            <BusinessIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
              Get Info from…
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Pick a provider and paste a company page URL. (Scraping comes later.)
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.25}>
          {submitError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {submitError}
            </Alert>
          )}

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.06)',
              background: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(10px)',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  label="Provider"
                  value={provider}
                  disabled={submitting}
                  onChange={(e) => {
                    setTouched(true);
                    setProvider(e.target.value as CompanyInfoProvider);
                  }}
                >
                  <MenuItem value="unknown">Auto-detect from URL</MenuItem>
                  {PROVIDERS.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Company page URL"
                value={url}
                disabled={submitting}
                onChange={(e) => {
                  setTouched(true);
                  setUrl(e.target.value);
                }}
                onBlur={() => {
                  setTouched(true);
                  setUrl(prev => normalizeUrl(prev));
                }}
                placeholder="https://www.linkedin.com/company/..."
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                      <LinkIcon fontSize="small" />
                    </Box>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Detected:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {detectedProvider === 'unknown'
                      ? '—'
                      : PROVIDERS.find(p => p.id === detectedProvider)?.label ?? '—'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  Selected: {providerLabel}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.25 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}>
          Cancel
        </Button>
        <Button
          onClick={async () => {
            const normalized = normalizeUrl(url);
            const value: CompanyInfoImportDraft = {
              provider: provider === 'unknown' ? detectProviderFromUrl(normalized) : provider,
              url: normalized,
            };

            try {
              setSubmitting(true);
              setSubmitError(null);

              if (onScrape) {
                await onScrape(value);
              }

              onConfirm(value);
            } catch (e) {
              setSubmitError(e instanceof Error ? e.message : 'Scraping failed');
            } finally {
              setSubmitting(false);
            }
          }}
          disabled={!canContinue}
          variant="contained"
          sx={{
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 900,
            px: 3,
            background: 'linear-gradient(135deg, rgba(255, 122, 24, 0.95) 0%, rgba(255, 61, 0, 0.9) 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(255, 122, 24, 1) 0%, rgba(255, 61, 0, 0.98) 100%)',
            },
          }}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: 'white' }} />
              <span>Scraping…</span>
            </Box>
          ) : (
            'Use this URL'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyInfoImportModal;
